/**
 * lindie-metaobject-filter.js
 *
 * Custom filter widget for Author / Narrator (and any future metaobject type).
 * Bypasses Shopify S&D's 100-value display cap by fetching metaobjects directly.
 *
 * Strategy:
 *   - Specific collections  → try collection.products.filters API (1 request, server counts)
 *                             fallback → fetch all metaobjects (no counts)
 *   - /collections/all      → fetch all metaobjects directly (no counts, scales infinitely)
 *
 * Config injected by Liquid via window.__lindieStore (see lindie-collection-grid.liquid).
 * Cache: sessionStorage keyed by type + collection handle, 10-min TTL.
 */

(function () {
  'use strict';

  var CFG = window.__lindieStore || {};
  var API_ROOT = 'https://' + CFG.shopDomain + '/api/' + (CFG.apiVersion || '2026-01') + '/graphql.json';
  var CACHE_TTL = 10 * 60 * 1000; // 10 minutes
  var CACHE_PREFIX = 'lindie_mof2_'; // bump suffix to bust stale cache

  /* ── Storefront API helper ─────────────────────────────────── */

  function gql(query) {
    return fetch(API_ROOT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CFG.storefrontToken
      },
      body: JSON.stringify({ query: query })
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function (json) {
      if (json.errors) throw new Error(JSON.stringify(json.errors));
      return json;
    });
  }

  /* ── sessionStorage cache ──────────────────────────────────── */

  function cacheGet(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (Date.now() > obj.exp) { sessionStorage.removeItem(key); return null; }
      return obj.data;
    } catch (e) { return null; }
  }

  function cacheSet(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ data: data, exp: Date.now() + CACHE_TTL }));
    } catch (e) { /* storage full — skip cache */ }
  }

  /* ── URL filter parser ─────────────────────────────────────── */

  /**
   * Parse filter.p.m.* params from a URL into Storefront API filter input objects.
   * e.g. filter.p.m.custom.author=gid://... →
   *      { productMetafield: { namespace: "custom", key: "author", value: "gid://..." } }
   */
  function parseFiltersFromUrl(url) {
    var params = new URL(url).searchParams;
    var filters = [];
    params.forEach(function (value, key) {
      var m = key.match(/^filter\.p\.m\.(.+)$/);
      if (!m) return;
      var parts = m[1].split('.');
      if (parts.length < 2) return;
      filters.push({
        productMetafield: {
          namespace: parts[0],
          key: parts.slice(1).join('.'),
          value: value
        }
      });
    });
    return filters;
  }

  /* ── Data fetchers ─────────────────────────────────────────── */

  /**
   * Try collection.products.filters — single request, server-aggregated counts.
   * Works for specific collections. Returns null if no data (capped or unavailable).
   * Pass activeFilters to get contextual counts (e.g. narrator counts within
   * selected authors). Omit / pass [] for the full unfiltered counts.
   */
  function fetchCollectionFilters(collectionHandle, filterKey, activeFilters) {
    var filtersGql = '';
    if (activeFilters && activeFilters.length) {
      var inputs = activeFilters.map(function (f) {
        var mf = f.productMetafield;
        return '{productMetafield:{namespace:"' + mf.namespace
          + '",key:"' + mf.key
          + '",value:"' + mf.value.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"}}';
      }).join(',');
      filtersGql = ',filters:[' + inputs + ']';
    }

    var query = '{'
      + 'collection(handle:"' + collectionHandle + '"){'
      + '  products(first:0' + filtersGql + '){'
      + '    filters{id label values{id label count input}}'
      + '  }'
      + '}'
      + '}';

    return gql(query).then(function (res) {
      var filters = (
        res.data &&
        res.data.collection &&
        res.data.collection.products &&
        res.data.collection.products.filters
      ) || [];

      var match = null;
      for (var i = 0; i < filters.length; i++) {
        if (filters[i].id.indexOf(filterKey) !== -1) { match = filters[i]; break; }
      }
      if (!match || !match.values || match.values.length === 0) return null;

      return match.values.map(function (v) {
        // Extract GID from the input JSON: { "productMetafield": { "value": "gid://..." } }
        var gid = null;
        try {
          var inp = JSON.parse(v.input);
          gid = (inp.productMetafield && inp.productMetafield.value) || null;
        } catch (e) {}
        return { id: gid || v.id, name: v.label, count: v.count };
      }).filter(function (v) { return v.id; });
    }).catch(function () { return null; });
  }

  /**
   * Fetch all metaobjects of a given type, paginated (250/request).
   * Used for /collections/all and as a fallback for specific collections.
   * Returns items sorted alphabetically, no counts.
   */
  function fetchAllMetaobjects(type, displayField) {
    var results = [];

    function page(cursor) {
      var after = cursor ? ', after: "' + cursor + '"' : '';
      var query = '{'
        + 'metaobjects(type: "' + type + '", first: 250' + after + ') {'
        + '  nodes { id fields { key value } }'
        + '  pageInfo { hasNextPage endCursor }'
        + '}'
        + '}';

      return gql(query).then(function (res) {
        var data = res.data && res.data.metaobjects;
        if (!data) return results;

        data.nodes.forEach(function (node) {
          var val = null;
          for (var i = 0; i < node.fields.length; i++) {
            if (node.fields[i].key === displayField) { val = node.fields[i].value; break; }
          }
          if (val) results.push({ id: node.id, name: val, count: null });
        });

        if (data.pageInfo.hasNextPage) return page(data.pageInfo.endCursor);

        results.sort(function (a, b) { return a.name.localeCompare(b.name); });
        return results;
      });
    }

    return page(null);
  }

  /* ── LindieMetaobjectFilter class ─────────────────────────── */

  function LindieMetaobjectFilter(container) {
    this.container  = container;
    this.type       = container.dataset.moType;        // 'author'
    this.label      = container.dataset.moLabel;       // 'Author'
    this.field      = container.dataset.moField || 'name'; // 'name'
    this.key        = container.dataset.moKey;         // 'custom.author'
    this.trigger    = container.querySelector('.coll-filter-group__trigger');
    this.list       = container.querySelector('[data-mo-list]');
    this.searchInput = container.querySelector('.coll-mo-search');
    this.allItems   = null;
    this.loaded     = false;

    if (!this.trigger || !this.list) return;
    container._moFilterInstance = this; // exposed for AJAX updater in collection-grid.js
    this._init();
  }

  LindieMetaobjectFilter.prototype._init = function () {
    var self = this;
    var url = new URL(window.location.href);
    var paramKey = 'filter.p.m.' + this.key;
    var activeGids = url.searchParams.getAll(paramKey);

    // Auto-open + load immediately if this filter is active in the URL
    if (activeGids.length > 0) {
      this.container.classList.add('coll-filter-group--open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this._showActiveBadge(activeGids.length);
      this._load();
    }

    // Accordion: open/close + lazy load on first open
    this.trigger.addEventListener('click', function () {
      var isOpen = self.container.classList.contains('coll-filter-group--open');
      self.container.classList.toggle('coll-filter-group--open');
      self.trigger.setAttribute('aria-expanded', String(!isOpen));
      if (!isOpen && !self.loaded) self._load();
    });
  };

  LindieMetaobjectFilter.prototype._showActiveBadge = function (count) {
    var labelSpan = this.trigger.querySelector('span:first-child');
    if (!labelSpan) return;
    var badge = document.createElement('span');
    badge.className = 'coll-mo-active-badge';
    badge.textContent = count;
    labelSpan.appendChild(badge);
  };

  LindieMetaobjectFilter.prototype._load = function () {
    var self = this;
    var cacheKey = CACHE_PREFIX + this.type + '_' + (CFG.isAllCollection ? 'all' : CFG.collectionHandle);
    var cached = cacheGet(cacheKey);

    if (cached) {
      self._render(cached);
      // If URL already has active filters, refresh counts in context
      if (!CFG.isAllCollection && parseFiltersFromUrl(window.location.href).length > 0) {
        self._reloadCounts(window.location.href);
      }
      return;
    }

    var promise;
    if (CFG.isAllCollection) {
      // /collections/all — metaobject list only, no per-product counting
      promise = fetchAllMetaobjects(self.type, self.field);
    } else {
      // Specific collection — try server-aggregated filter counts first
      promise = fetchCollectionFilters(CFG.collectionHandle, self.key)
        .then(function (data) {
          if (data && data.length > 0) return data;
          // Filters API returned empty (products not in collection, or capped) — fallback
          return fetchAllMetaobjects(self.type, self.field);
        });
    }

    promise
      .then(function (data) {
        // Only cache real results — never cache empty arrays (could be a transient error)
        if (data && data.length > 0) cacheSet(cacheKey, data);
        self._render(data);
        // If URL already has active filters, refresh counts in context
        if (!CFG.isAllCollection && parseFiltersFromUrl(window.location.href).length > 0) {
          self._reloadCounts(window.location.href);
        }
      })
      .catch(function (err) {
        console.warn('[lindie-mo-filter] ' + self.type + ':', err);
        if (self.list) {
          self.list.innerHTML = '<span class="coll-mo-error">Could not load filters. Reload to try again.</span>';
        }
      });
  };

  /**
   * Refresh counts for this filter based on the currently active filters in `url`.
   * Called after AJAX navigation so cross-dimension counts stay accurate.
   * Items that drop to 0 are hidden (unless they're currently active selections).
   */
  LindieMetaobjectFilter.prototype._reloadCounts = function (url) {
    var self = this;
    if (!self.loaded || !self.allItems) return;

    var activeSet = new Set(new URL(url).searchParams.getAll('filter.p.m.' + self.key));

    if (CFG.isAllCollection) {
      // No counts for all-collection — just refresh active states
      self._renderItems(self.allItems, activeSet);
      return;
    }

    var activeFilters = parseFiltersFromUrl(url);

    fetchCollectionFilters(CFG.collectionHandle, self.key, activeFilters)
      .then(function (data) {
        if (!data) {
          // Filters API unavailable — just refresh active states, keep old counts
          self._renderItems(self.allItems, activeSet);
          return;
        }

        // Build fresh count map from contextual result
        var countMap = {};
        data.forEach(function (item) { countMap[item.id] = item.count; });

        // Merge: update counts; hide items with 0 unless they're actively selected
        var displayItems = self.allItems
          .map(function (item) {
            return {
              id: item.id,
              name: item.name,
              count: countMap[item.id] != null ? countMap[item.id] : 0
            };
          })
          .filter(function (item) {
            return activeSet.has(item.id) || item.count > 0;
          });

        self._renderItems(displayItems, activeSet);
      })
      .catch(function () {
        // Network error — degrade gracefully
        self._renderItems(self.allItems, activeSet);
      });
  };

  LindieMetaobjectFilter.prototype._render = function (items) {
    this.allItems = items || [];
    this.loaded = true;

    if (!items || items.length === 0) {
      if (this.list) this.list.innerHTML = '<span class="coll-mo-empty">No results</span>';
      return;
    }

    var url = new URL(window.location.href);
    var activeSet = new Set(url.searchParams.getAll('filter.p.m.' + this.key));

    this._renderItems(this.allItems, activeSet);
    this._initSearch(activeSet);
  };

  LindieMetaobjectFilter.prototype._renderItems = function (items, activeSet) {
    var self = this;
    if (!this.list) return;

    var paramKey = 'filter.p.m.' + this.key;
    var html = '';

    items.forEach(function (item) {
      var isActive = activeSet.has(item.id);

      // Build the toggled URL (add or remove this filter value)
      var filterUrl = new URL(window.location.href);
      filterUrl.searchParams.delete('page'); // reset pagination on filter change

      if (isActive) {
        var remaining = filterUrl.searchParams.getAll(paramKey).filter(function (v) {
          return v !== item.id;
        });
        filterUrl.searchParams.delete(paramKey);
        remaining.forEach(function (v) { filterUrl.searchParams.append(paramKey, v); });
      } else {
        filterUrl.searchParams.append(paramKey, item.id);
      }

      var checkmark = isActive
        ? '<svg width="9" height="7" viewBox="0 0 9 7" fill="none" aria-hidden="true">'
          + '<path d="M1 3.5L3.5 6L8 1" stroke="var(--lindie-charcoal-deep)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
          + '</svg>'
        : '';

      var countHtml = item.count != null
        ? '<span class="coll-filter-item__count">' + item.count + '</span>'
        : '';

      html += '<a href="' + filterUrl.toString() + '"'
        + ' class="coll-filter-item' + (isActive ? ' coll-filter-item--active' : '') + '"'
        + ' aria-label="' + self._esc(item.name) + (item.count != null ? ', ' + item.count + ' product' + (item.count !== 1 ? 's' : '') : '') + (isActive ? ', active' : '') + '"'
        + '>'
        + '<span class="coll-filter-item__box" aria-hidden="true">' + checkmark + '</span>'
        + '<span class="coll-filter-item__label">' + self._esc(item.name) + '</span>'
        + countHtml
        + '</a>';
    });

    this.list.innerHTML = html;
  };

  LindieMetaobjectFilter.prototype._initSearch = function (activeSet) {
    var self = this;
    if (!this.searchInput) return;
    this.searchInput.addEventListener('input', function () {
      var q = self.searchInput.value.toLowerCase().trim();
      var filtered = q
        ? self.allItems.filter(function (item) {
            return item.name.toLowerCase().indexOf(q) !== -1;
          })
        : self.allItems;
      self._renderItems(filtered, activeSet);
    });
  };

  LindieMetaobjectFilter.prototype._esc = function (str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  /* ── Boot ──────────────────────────────────────────────────── */

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-mo-filter]').forEach(function (el) {
      new LindieMetaobjectFilter(el);
    });
  });

})();
